/**
 * TemplateCreator — wizard de criação de template Cloud API.
 *
 * Steps:
 *   1. Básicos: name + language + category
 *   2. Body: texto com {{1}}, {{2}}, samples
 *   3. Header: opcional (TEXT/IMAGE/VIDEO/DOCUMENT/LOCATION)
 *   4. Footer: opcional, texto curto
 *   5. Buttons: opcional, até 10 (QUICK_REPLY/URL/PHONE_NUMBER)
 *   6. Preview + submit
 *
 * Submit chama WhatsAppCloudApiService.createTemplate; Meta retorna
 * status PENDING (revisão) ou APPROVED imediato (raro).
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconTrash,
  IconAlertTriangle,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import WhatsAppCloudApiService from '../../../../../../WhatsAppCloudApi/services/WhatsAppCloudApiService';
import TemplatePreview from './TemplatePreview';
import './templates.css';

const STEPS = [
  { id: 'basics', label: 'Básicos' },
  { id: 'body', label: 'Corpo' },
  { id: 'header', label: 'Cabeçalho' },
  { id: 'footer', label: 'Rodapé' },
  { id: 'buttons', label: 'Botões' },
  { id: 'review', label: 'Revisão' },
];

const LANGUAGES = [
  { code: 'pt_BR', name: 'Português (Brasil)' },
  { code: 'en', name: 'Inglês' },
  { code: 'en_US', name: 'Inglês (EUA)' },
  { code: 'es', name: 'Espanhol' },
  { code: 'es_AR', name: 'Espanhol (Argentina)' },
];

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', desc: 'Promoções, campanhas, novidades' },
  { value: 'UTILITY', label: 'Utilidade', desc: 'Confirmações, status, alertas' },
  { value: 'AUTHENTICATION', label: 'Autenticação', desc: 'OTP, verificações' },
];

const HEADER_FORMATS = [
  { value: 'NONE', label: 'Sem cabeçalho' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagem' },
  { value: 'VIDEO', label: 'Vídeo' },
  { value: 'DOCUMENT', label: 'Documento' },
  { value: 'LOCATION', label: 'Localização' },
];

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Resposta rápida' },
  { value: 'URL', label: 'Abrir URL' },
  { value: 'PHONE_NUMBER', label: 'Ligar' },
];

const TEMPLATE_NAME_REGEX = /^[a-z0-9_]{1,512}$/;

// Extrai placeholders {{1}}, {{2}} de um texto e retorna array ordenado de índices únicos
function extractVariables(text) {
  if (!text) return [];
  const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
  return [...new Set(matches)].sort((a, b) => a - b);
}

const initialState = {
  name: '',
  language: 'pt_BR',
  category: 'MARKETING',
  bodyText: '',
  bodyVarSamples: {}, // { 1: 'João', 2: 'Premium' }
  headerFormat: 'NONE',
  headerText: '',
  headerVarSamples: {},
  footerText: '',
  buttons: [], // [{type, text, url?, phone_number?}]
};

const Field = ({ label, children, hint, error }) => (
  <div style={{ marginBottom: 12 }}>
    <label
      style={{
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 4,
      }}
    >
      {label}
    </label>
    {children}
    {hint && (
      <p style={{ fontSize: 13, color: '#9ca3af', margin: '4px 0 0' }}>{hint}</p>
    )}
    {error && (
      <p style={{ fontSize: 13, color: '#dc2626', margin: '4px 0 0' }}>{error}</p>
    )}
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const TemplateCreator = ({ agentId, onCancel, onCreated }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const update = (patch) => setData((prev) => ({ ...prev, ...patch }));

  // Variables encontradas
  const bodyVars = useMemo(() => extractVariables(data.bodyText), [data.bodyText]);
  const headerVars = useMemo(
    () =>
      data.headerFormat === 'TEXT' ? extractVariables(data.headerText) : [],
    [data.headerFormat, data.headerText],
  );

  // Validação por step
  const stepErrors = useMemo(() => {
    const errs = {};
    if (step === 0) {
      if (!data.name) errs.name = 'Nome obrigatório';
      else if (!TEMPLATE_NAME_REGEX.test(data.name))
        errs.name = 'Apenas lowercase, dígitos e underscores';
      if (!data.language) errs.language = 'Idioma obrigatório';
      if (!data.category) errs.category = 'Categoria obrigatória';
    }
    if (step === 1) {
      if (!data.bodyText.trim()) errs.bodyText = 'Texto do corpo obrigatório';
      if (data.bodyText.length > 1024)
        errs.bodyText = 'Máximo 1024 caracteres';
      // Toda variável precisa de sample
      for (const idx of bodyVars) {
        if (!data.bodyVarSamples[idx])
          errs[`bodyVar_${idx}`] = `Sample obrigatório para {{${idx}}}`;
      }
    }
    if (step === 2 && data.headerFormat === 'TEXT') {
      if (!data.headerText.trim()) errs.headerText = 'Texto do cabeçalho';
      if (data.headerText.length > 60) errs.headerText = 'Máximo 60';
      for (const idx of headerVars) {
        if (!data.headerVarSamples[idx])
          errs[`headerVar_${idx}`] = `Sample obrigatório para {{${idx}}}`;
      }
    }
    if (step === 3 && data.footerText && data.footerText.length > 60) {
      errs.footerText = 'Máximo 60 caracteres';
    }
    if (step === 4) {
      data.buttons.forEach((btn, i) => {
        if (!btn.text) errs[`btn_text_${i}`] = 'Texto obrigatório';
        if (btn.text && btn.text.length > 25)
          errs[`btn_text_${i}`] = 'Máximo 25 caracteres';
        if (btn.type === 'URL' && !btn.url) errs[`btn_url_${i}`] = 'URL obrigatória';
        if (btn.type === 'PHONE_NUMBER' && !btn.phone_number)
          errs[`btn_phone_${i}`] = 'Número obrigatório';
      });
    }
    return errs;
  }, [step, data, bodyVars, headerVars]);

  const canAdvance = Object.keys(stepErrors).length === 0;

  // Componentes derivados para preview
  const previewTemplate = useMemo(() => {
    const components = [];

    if (data.headerFormat !== 'NONE') {
      const h = { type: 'HEADER', format: data.headerFormat };
      if (data.headerFormat === 'TEXT') {
        h.text = data.headerText;
        if (headerVars.length > 0) {
          h.example = {
            header_text: headerVars.map((i) => data.headerVarSamples[i] || ''),
          };
        }
      }
      components.push(h);
    }

    const body = { type: 'BODY', text: data.bodyText };
    if (bodyVars.length > 0) {
      body.example = {
        body_text: [bodyVars.map((i) => data.bodyVarSamples[i] || '')],
      };
    }
    components.push(body);

    if (data.footerText) {
      components.push({ type: 'FOOTER', text: data.footerText });
    }

    if (data.buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: data.buttons.filter((b) => b.text),
      });
    }

    return {
      name: data.name,
      language: data.language,
      category: data.category,
      components,
    };
  }, [data, bodyVars, headerVars]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const resp = await WhatsAppCloudApiService.createTemplate(
        agentId,
        previewTemplate,
      );
      Swal.fire({
        icon: 'success',
        title: 'Template enviado para revisão!',
        html: `
          <div style="font-size:14px;line-height:1.5">
            <p>ID: <code>${resp.data?.template_id || '—'}</code></p>
            <p>Status: <strong>${resp.data?.status || 'PENDING'}</strong></p>
            <p style="margin-top:12px;color:#6b7280">
              A Meta vai analisar o template. Você receberá uma notificação
              quando o status mudar (geralmente em poucos minutos a algumas horas).
            </p>
          </div>
        `,
        confirmButtonColor: '#1877f2',
      });
      if (onCreated) onCreated(resp.data);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao criar template',
        text:
          err.response?.data?.error ||
          err.message ||
          'Tente novamente.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [agentId, previewTemplate, onCreated]);

  const renderBasicsStep = () => (
    <div className="tmpl-wizard__split">
      <div>
        <Field
          label="Nome do template"
          hint="Sem espaços. Exemplo: boas_vindas, confirmacao_pedido"
          error={stepErrors.name}
        >
          <input
            type="text"
            style={inputStyle}
            value={data.name}
            onChange={(e) => update({ name: e.target.value.toLowerCase() })}
            placeholder="boas_vindas"
            maxLength={512}
          />
        </Field>

        <Field label="Idioma" error={stepErrors.language}>
          <select
            style={inputStyle}
            value={data.language}
            onChange={(e) => update({ language: e.target.value })}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} ({l.code})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Categoria" error={stepErrors.category}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {CATEGORIES.map((c) => (
              <label
                key={c.value}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: 10,
                  border: '1px solid',
                  borderColor: data.category === c.value ? '#1877f2' : '#e5e7eb',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: data.category === c.value ? '#eff6ff' : '#fff',
                }}
              >
                <input
                  type="radio"
                  name="category"
                  value={c.value}
                  checked={data.category === c.value}
                  onChange={() => update({ category: c.value })}
                  style={{ marginTop: 2 }}
                />
                <span>
                  <strong style={{ fontSize: 13 }}>{c.label}</strong>
                  <br />
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{c.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>
      </div>
    </div>
  );

  const renderBodyStep = () => (
    <div className="tmpl-wizard__split">
      <div>
        <Field
          label={`Corpo do template (${data.bodyText.length}/1024)`}
          hint="Use {{1}}, {{2}} para variáveis. Ex: Olá {{1}}, seu pedido {{2}} foi confirmado."
          error={stepErrors.bodyText}
        >
          <textarea
            style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
            value={data.bodyText}
            onChange={(e) => update({ bodyText: e.target.value })}
            placeholder="Olá {{1}}, seu pedido {{2}} foi confirmado!"
            maxLength={1024}
          />
        </Field>

        {bodyVars.length > 0 && (
          <div className="tmpl-wizard__var-list">
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
              Valores de exemplo (necessários para revisão Meta):
            </span>
            {bodyVars.map((idx) => (
              <div key={idx} className="tmpl-wizard__var-row">
                <label>{`{{${idx}}}`}</label>
                <input
                  type="text"
                  value={data.bodyVarSamples[idx] || ''}
                  onChange={(e) =>
                    update({
                      bodyVarSamples: {
                        ...data.bodyVarSamples,
                        [idx]: e.target.value,
                      },
                    })
                  }
                  placeholder={`Exemplo para {{${idx}}}`}
                />
                {stepErrors[`bodyVar_${idx}`] && (
                  <span style={{ fontSize: 11, color: '#dc2626' }}>
                    {stepErrors[`bodyVar_${idx}`]}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="tmpl-wizard__preview-side">
        <p className="tmpl-wizard__preview-label">Pré-visualização</p>
        <TemplatePreview template={previewTemplate} dense />
      </div>
    </div>
  );

  const renderHeaderStep = () => (
    <div className="tmpl-wizard__split">
      <div>
        <Field
          label="Tipo do cabeçalho"
          hint="Cabeçalho é opcional. Aparece no topo da mensagem."
        >
          <select
            style={inputStyle}
            value={data.headerFormat}
            onChange={(e) => update({ headerFormat: e.target.value })}
          >
            {HEADER_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>

        {data.headerFormat === 'TEXT' && (
          <>
            <Field
              label={`Texto do cabeçalho (${data.headerText.length}/60)`}
              hint="Pode usar {{1}} para variável."
              error={stepErrors.headerText}
            >
              <input
                type="text"
                style={inputStyle}
                value={data.headerText}
                onChange={(e) => update({ headerText: e.target.value })}
                placeholder="Olá {{1}}!"
                maxLength={60}
              />
            </Field>

            {headerVars.length > 0 && (
              <div className="tmpl-wizard__var-list">
                {headerVars.map((idx) => (
                  <div key={idx} className="tmpl-wizard__var-row">
                    <label>{`{{${idx}}}`}</label>
                    <input
                      type="text"
                      value={data.headerVarSamples[idx] || ''}
                      onChange={(e) =>
                        update({
                          headerVarSamples: {
                            ...data.headerVarSamples,
                            [idx]: e.target.value,
                          },
                        })
                      }
                      placeholder={`Exemplo para {{${idx}}}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(data.headerFormat) && (
          <div
            style={{
              padding: 12,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
              fontSize: 12,
              color: '#92400e',
            }}
          >
            <IconAlertTriangle size={14} style={{ marginRight: 6 }} />
            Para cabeçalho de mídia, é necessário fornecer um arquivo de
            exemplo via upload ao Meta. Esta UI ainda não suporta upload —
            por enquanto crie o template com mídia direto no Meta Business Manager.
          </div>
        )}

        {data.headerFormat === 'LOCATION' && (
          <p style={{ fontSize: 12, color: '#6b7280' }}>
            Cabeçalho de localização será preenchido com latitude/longitude
            no momento do envio (não precisa configurar aqui).
          </p>
        )}
      </div>

      <div className="tmpl-wizard__preview-side">
        <p className="tmpl-wizard__preview-label">Pré-visualização</p>
        <TemplatePreview template={previewTemplate} dense />
      </div>
    </div>
  );

  const renderFooterStep = () => (
    <div className="tmpl-wizard__split">
      <div>
        <Field
          label={`Rodapé (${data.footerText.length}/60)`}
          hint="Texto curto opcional, geralmente um disclaimer. Sem variáveis."
          error={stepErrors.footerText}
        >
          <input
            type="text"
            style={inputStyle}
            value={data.footerText}
            onChange={(e) => update({ footerText: e.target.value })}
            placeholder="Esta é uma mensagem automática"
            maxLength={60}
          />
        </Field>
      </div>

      <div className="tmpl-wizard__preview-side">
        <p className="tmpl-wizard__preview-label">Pré-visualização</p>
        <TemplatePreview template={previewTemplate} dense />
      </div>
    </div>
  );

  const addButton = () => {
    if (data.buttons.length >= 10) {
      Swal.fire({ icon: 'info', title: 'Máximo 10 botões' });
      return;
    }
    update({
      buttons: [...data.buttons, { type: 'QUICK_REPLY', text: '' }],
    });
  };

  const removeButton = (idx) => {
    update({
      buttons: data.buttons.filter((_, i) => i !== idx),
    });
  };

  const updateButton = (idx, patch) => {
    update({
      buttons: data.buttons.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    });
  };

  const renderButtonsStep = () => (
    <div className="tmpl-wizard__split">
      <div>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
          Botões aparecem abaixo da mensagem e permitem ações rápidas.
          Cliente pode responder, abrir URL ou ligar.
        </p>

        {data.buttons.length === 0 && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              border: '1px dashed #e5e7eb',
              borderRadius: 8,
              color: '#9ca3af',
              fontSize: 13,
            }}
          >
            Sem botões. Pular para próximo step ou adicionar.
          </div>
        )}

        {data.buttons.map((btn, i) => (
          <div key={i} className="tmpl-button-row">
            <select
              style={inputStyle}
              value={btn.type}
              onChange={(e) => updateButton(i, { type: e.target.value })}
            >
              {BUTTON_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              style={inputStyle}
              value={btn.text || ''}
              onChange={(e) => updateButton(i, { text: e.target.value })}
              placeholder="Texto do botão"
              maxLength={25}
            />
            {btn.type === 'URL' && (
              <input
                type="url"
                style={inputStyle}
                value={btn.url || ''}
                onChange={(e) => updateButton(i, { url: e.target.value })}
                placeholder="https://..."
              />
            )}
            {btn.type === 'PHONE_NUMBER' && (
              <input
                type="tel"
                style={inputStyle}
                value={btn.phone_number || ''}
                onChange={(e) => updateButton(i, { phone_number: e.target.value })}
                placeholder="+5511999999999"
              />
            )}
            {btn.type === 'QUICK_REPLY' && <span />}
            <button
              type="button"
              onClick={() => removeButton(i)}
              style={{
                padding: 8,
                border: '1px solid #fecaca',
                background: '#fef2f2',
                color: '#dc2626',
                borderRadius: 6,
                cursor: 'pointer',
              }}
              title="Remover botão"
            >
              <IconTrash size={14} />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addButton}
          className="tmpl-wizard__btn"
          style={{ marginTop: 8 }}
        >
          <IconPlus size={14} />
          Adicionar botão
        </button>
      </div>

      <div className="tmpl-wizard__preview-side">
        <p className="tmpl-wizard__preview-label">Pré-visualização</p>
        <TemplatePreview template={previewTemplate} dense />
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
        Revise antes de enviar. Após criar, a Meta analisa o template
        (status PENDING → APPROVED ou REJECTED). Aprovação geralmente leva
        de minutos a horas.
      </p>

      <div className="tmpl-wizard__split">
        <div>
          <div className="cloudapi-section">
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">Nome</span>
              <span className="cloudapi-section__row-value">{data.name}</span>
            </div>
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">Idioma</span>
              <span className="cloudapi-section__row-value">{data.language}</span>
            </div>
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">Categoria</span>
              <span className="cloudapi-section__row-value">{data.category}</span>
            </div>
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">Componentes</span>
              <span className="cloudapi-section__row-value">
                {previewTemplate.components.map((c) => c.type).join(', ')}
              </span>
            </div>
          </div>
        </div>
        <div className="tmpl-wizard__preview-side">
          <p className="tmpl-wizard__preview-label">Pré-visualização final</p>
          <TemplatePreview template={previewTemplate} dense />
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (STEPS[step].id) {
      case 'basics': return renderBasicsStep();
      case 'body': return renderBodyStep();
      case 'header': return renderHeaderStep();
      case 'footer': return renderFooterStep();
      case 'buttons': return renderButtonsStep();
      case 'review': return renderReviewStep();
      default: return null;
    }
  };

  return (
    <div className="tmpl-wizard">
      <div className="tmpl-wizard__steps">
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`tmpl-wizard__step ${
              i === step ? 'tmpl-wizard__step--active' : ''
            } ${i < step ? 'tmpl-wizard__step--done' : ''}`}
            onClick={() => i < step && setStep(i)}
            disabled={i > step}
          >
            <span className="tmpl-wizard__step-number">
              {i < step ? <IconCheck size={10} /> : i + 1}
            </span>
            {s.label}
          </button>
        ))}
      </div>

      <div className="tmpl-wizard__panel">{renderStepContent()}</div>

      <div className="tmpl-wizard__nav">
        <button
          type="button"
          className="tmpl-wizard__btn"
          onClick={step === 0 ? onCancel : () => setStep((s) => s - 1)}
          disabled={submitting}
        >
          <IconArrowLeft size={14} style={{ marginRight: 4 }} />
          {step === 0 ? 'Cancelar' : 'Voltar'}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            className="tmpl-wizard__btn tmpl-wizard__btn--primary"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canAdvance}
          >
            Próximo
          </button>
        ) : (
          <button
            type="button"
            className="tmpl-wizard__btn tmpl-wizard__btn--primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : <IconCheck size={14} />}
            {submitting ? 'Enviando...' : 'Enviar para revisão'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TemplateCreator;
